/**
 * RegisterCard Component
 * 회원가입 유형 선택 카드 (본사/협력사)
 *
 * @description
 * 회원가입 페이지에서 본사 또는 협력사 계정 유형을
 * 선택하기 위한 카드형 UI 컴포넌트
 */
import React, { memo, useCallback, useMemo, type ReactNode, type CSSProperties } from 'react';

// ============================================================
// 타입 정의
// ============================================================

interface RegisterCardProps {
    /** 카드 제목 */
    title: string;
    /** 카드 설명 */
    description: string;
    /** 아이콘 텍스트 */
    icon: string;
    /** 가라데이션 색상 */
    gradientColors: { from: string; to: string };
    /** 테두리 색상 */
    borderColor: string;
    /** 배경 색상 */
    backgroundColor: string;
    /** 확인 완료 여부 */
    isChecked: boolean;
    /** 클릭 핸들러 */
    onClick?: () => void;
    /** 입력 필드 라벨 */
    inputLabel: string;
    /** 입력 필드 placeholder */
    inputPlaceholder: string;
    /** 입력값 */
    inputValue: string;
    /** 입력 변경 핸들러 */
    onInputChange: (value: string) => void;
    /** 로딩 상태 */
    isLoading: boolean;
    /** 확인 버튼 클릭 핸들러 */
    onCheckClick: () => void;
    /** 확인 버튼 색상 */
    checkButtonColor: string;
    /** 에러 메시지 */
    error?: string;
    /** 성공 메시지 */
    successMessage?: ReactNode;
    /** 입력 타입 */
    inputType?: string;
}

// ============================================================
// 스타일 상수
// ============================================================

const STYLES = {
    card: {
        padding: '2rem',
        borderRadius: '12px',
        transition: 'all 0.3s ease',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        marginBottom: '1.5rem',
    },
    iconBox: {
        width: '60px',
        height: '60px',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontSize: '24px',
        fontWeight: '700',
    },
    titleContainer: {
        flex: 1,
    },
    title: {
        fontSize: '1.25rem',
        fontWeight: '600',
        color: '#1a1a1a',
        marginBottom: '0.5rem',
    },
    description: {
        fontSize: '0.9rem',
        color: '#666666',
        lineHeight: '1.5',
    },
    inputContainer: {
        marginBottom: '1rem',
    },
    label: {
        display: 'block',
        fontSize: '0.9rem',
        fontWeight: '500',
        color: '#1a1a1a',
        marginBottom: '0.5rem'
    },
    inputWrapper: {
        display: 'flex',
        gap: '0.5rem',
    },
    input: {
        flex: 1,
        padding: '12px 16px',
        border: '2px solid #e5e7eb',
        borderRadius: '8px',
        fontSize: '1rem',
    },
    successMessage: {
        marginTop: '0.75rem',
        padding: '0.75rem',
        borderRadius: '8px',
        fontSize: '0.875rem',
    },
} as const;

// ============================================================
// 컴포넌트 구현
// ============================================================

/**
 * 회원가입 유형 선택 카드 컴포넌트
 */
const RegisterCard: React.FC<RegisterCardProps> = ({
    title,
    description,
    icon,
    gradientColors,
    borderColor,
    backgroundColor,
    isChecked,
    onClick,
    inputLabel,
    inputPlaceholder,
    inputValue,
    onInputChange,
    isLoading,
    onCheckClick,
    checkButtonColor,
    error,
    successMessage,
    inputType = 'text',
}) => {
    // 카드 스타일 계산
    const cardStyle = useMemo<CSSProperties>(() => ({
        ...STYLES.card,
        border: `2px solid ${isChecked ? borderColor : '#e5e7eb'}`,
        background: isChecked ? backgroundColor : 'white',
        cursor: isChecked ? 'pointer' : 'default',
    }), [isChecked, borderColor, backgroundColor]);

    // 아이콘 박스 스타일
    const iconBoxStyle = useMemo<CSSProperties>(() => ({
        ...STYLES.iconBox,
        background: `linear-gradient(135deg, ${gradientColors.from} 0%, ${gradientColors.to} 100%)`,
    }), [gradientColors]);

    // 버튼 스타일
    const buttonStyle = useMemo<CSSProperties>(() => {
        const isDisabled = isLoading || isChecked || !inputValue;
        return {
            padding: '12px 20px',
            background: isChecked ? '#10B981' : checkButtonColor,
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '0.9rem',
            fontWeight: '600',
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            opacity: (isLoading || !inputValue) && !isChecked ? 0.6 : 1,
            whiteSpace: 'nowrap',
        };
    }, [isChecked, isLoading, inputValue, checkButtonColor]);

    // 입력 스타일
    const inputStyle = useMemo<CSSProperties>(() => ({
        ...STYLES.input,
        fontWeight: inputType === 'password' ? 500 : 'normal',
    }), [inputType]);

    // 마우스 이벤트 핸들러
    const handleMouseEnter = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        if (isChecked) {
            const target = e.currentTarget;
            target.style.borderColor = borderColor;
            target.style.background = backgroundColor.replace('F0', 'E3');
            target.style.transform = 'translateY(-4px)';
            target.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
        }
    }, [isChecked, borderColor, backgroundColor]);

    const handleMouseLeave = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        if (isChecked) {
            const target = e.currentTarget;
            target.style.borderColor = borderColor;
            target.style.background = backgroundColor;
            target.style.transform = 'translateY(0)';
            target.style.boxShadow = 'none';
        }
    }, [isChecked, borderColor, backgroundColor]);

    // 입력 변경 핸들러
    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        onInputChange(e.target.value);
    }, [onInputChange]);

    // 확인 버튼 클릭 핸들러
    const handleCheckClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        onCheckClick();
    }, [onCheckClick]);

    // 이벤트 전파 방지
    const stopPropagation = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
    }, []);

    // 버튼 텍스트
    const buttonText = isLoading ? '확인 중...' : isChecked ? '확인완료' : '확인';

    return (
        <div
            onClick={isChecked ? onClick : undefined}
            style={cardStyle}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {/* 카드 헤더 */}
            <div style={STYLES.header}>
                <div style={iconBoxStyle}>{icon}</div>
                <div style={STYLES.titleContainer}>
                    <h3 style={STYLES.title}>{title}</h3>
                    <p style={STYLES.description}>{description}</p>
                </div>
                {isChecked && (
                    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M9 5l7 7-7 7" />
                    </svg>
                )}
            </div>

            {/* 입력 필드 */}
            <div style={STYLES.inputContainer}>
                <label style={STYLES.label}>
                    {inputLabel} <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <div style={STYLES.inputWrapper} onClick={stopPropagation}>
                    <input
                        type={inputType}
                        className="form-input"
                        style={inputStyle}
                        placeholder={inputPlaceholder}
                        value={inputValue}
                        onChange={handleInputChange}
                        disabled={isLoading || isChecked}
                    />
                    <button
                        onClick={handleCheckClick}
                        disabled={isLoading || isChecked || !inputValue}
                        style={buttonStyle}
                    >
                        {buttonText}
                    </button>
                </div>
                {error && (
                    <div className="error-message" style={{ marginTop: '0.5rem' }}>
                        {error}
                    </div>
                )}
                {successMessage && (
                    <div style={STYLES.successMessage}>
                        {successMessage}
                    </div>
                )}
            </div>
        </div>
    );
};

// React.memo로 불필요한 리렌더링 방지
export default memo(RegisterCard);
